import type { JobDataResultQuery } from "../__generated__/jobDataResult.gqlgenerated";

/**
 * Converts GraphQL MatchComparisonEngineMatchResult to OneGraphMatchComparison format
 * Based on the Java implementation getOneGraphMatchComparisonResult
 *
 * @param matchComparisonResult - The GraphQL match comparison result
 * @param isAllYellowLightTstActive - Whether to force all traffic lights to YELLOW (default: true)
 * @returns The converted OneGraphMatchComparison object or undefined if no result
 */
export const getOneGraphMatchComparisonResult = (
  matchComparisonResult?: NonNullable<
    JobDataResultQuery["jobData"]
  >["results"][0]["matchComparisonResult"],
  isAllYellowLightTstActive: boolean = true,
): component.oneGraphMatchComparison.OneGraphMatchComparison | undefined => {
  if (!matchComparisonResult) {
    return undefined;
  }

  const result: component.oneGraphMatchComparison.OneGraphMatchComparison = {};

  if (matchComparisonResult.encouragementToApply) {
    const encouragementToApply: component.oneGraphMatchComparison.EncouragementToApply = {};

    if (isAllYellowLightTstActive) {
      encouragementToApply.trafficLight = "YELLOW" as const;
    } else if (matchComparisonResult.encouragementToApply.trafficLight) {
      encouragementToApply.trafficLight = matchComparisonResult.encouragementToApply.trafficLight;
    }

    encouragementToApply.score = matchComparisonResult.encouragementToApply.score ?? 0;
    encouragementToApply.strategy = matchComparisonResult.encouragementToApply.strategy ?? "";

    result.encouragementToApply = encouragementToApply;
  }

  if (
    matchComparisonResult.attributeComparisons &&
    matchComparisonResult.attributeComparisons.length > 0
  ) {
    result.attributeComparisons = matchComparisonResult.attributeComparisons.map(
      (attributeComparison) => {
        const comparison: component.oneGraphMatchComparison.AttributeComparison = {};

        if (attributeComparison.jobProvenance) {
          comparison.jobProvenance = attributeComparison.jobProvenance;
        }

        if (attributeComparison.jsProvenance) {
          comparison.jsProvenance = attributeComparison.jsProvenance;
        }

        if (attributeComparison.attribute) {
          const attribute: component.oneGraphMatchComparison.MatchComparisonAttribute = {};

          if (attributeComparison.attribute.label) {
            attribute.label = attributeComparison.attribute.label;
          }

          if (attributeComparison.attribute.suid) {
            attribute.suid = attributeComparison.attribute.suid;
          }

          if (attributeComparison.attribute.interestedParty) {
            attribute.interestedParty = attributeComparison.attribute.interestedParty;
          }

          attribute.profileAttributeTypeSuid =
            attributeComparison.attribute.profileAttributeTypeSuid ?? "";

          comparison.attribute = attribute;
        }

        if (attributeComparison.jobRequirementStrength) {
          comparison.jobRequirementStrength = attributeComparison.jobRequirementStrength;
        }

        if (attributeComparison.matchType) {
          comparison.matchType = attributeComparison.matchType;
        }

        return comparison;
      },
    );
  }

  return result;
};
